import React, { useEffect, useMemo, useState } from "react";
import apiService from "../services/apiService";
import {
  PieChart,
  Pie,
  Tooltip,
  Cell,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FaBoxOpen,
  FaWarehouse,
  FaUsers,
  FaFileInvoice,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./Dashboard.css";

function CountUp({ value, duration = 800 }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const end = Number(value) || 0;
    let startTimestamp = null;

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setDisplayValue(Math.floor(progress * end));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setDisplayValue(end);
      }
    };

    window.requestAnimationFrame(step);
  }, [value, duration]);

  return <>{displayValue.toLocaleString()}</>;
}

function SkeletonCards() {
  return (
    <div className="dashboard-cards">
      {[1, 2, 3, 4].map((i) => (
        <div className="dashboard-card dashboard-skeleton-card" key={i}>
          <div className="skeleton skeleton-line short"></div>
          <div className="skeleton skeleton-line medium"></div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [selectedCard, setSelectedCard] = useState("Total Items");
  const [chartData, setChartData] = useState([]);
  const [listData, setListData] = useState([]);
  const [itemSummary, setItemSummary] = useState(null);
  const [itemPage, setItemPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingSection, setLoadingSection] = useState(false);

  const supplierPageSize = 8;
  const colors = ["#7c3aed", "#06b6d4", "#f59e0b", "#10b981", "#ef4444"];

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setLoadingSummary(true);
        const data = await apiService.getDashboardSummary();
        setSummary(data);
      } catch (err) {
        console.error("Dashboard summary error:", err);
      } finally {
        setLoadingSummary(false);
      }
    };

    loadSummary();
  }, []);

  useEffect(() => {
    if (summary) {
      handleCardClick("Total Items");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  const loadItemSummary = async (page = 1) => {
    try {
      const data = await apiService.getItemStockSummary(page);
      setItemSummary(data);
      setItemPage(page);
    } catch (error) {
      console.error("Error loading item stock summary:", error);
    }
  };

  const handleCardClick = async (type) => {
    setSelectedCard(type);
    setChartData([]);
    setListData([]);
    setItemSummary(null);

    try {
      setLoadingSection(true);

      if (type === "Total Items") {
        await loadItemSummary(1);
      } else if (type === "Total Stock") {
        const data = await apiService.getDashboardChart("stock-by-warehouse");
        const formattedData = (data || []).map((item) => ({
          ...item,
          total: Number(item.total),
        }));
        setChartData(formattedData);
      } else if (type === "Suppliers") {
        const data = await apiService.getSuppliers();
        setListData(data || []);
        setSupplierPage(1);
      } else if (type === "Pending POs") {
        const data = await apiService.getPurchaseOrders();
        const pending = (data || []).filter((po) => po.Status === "Pending");
        setListData(pending);
      } else if (type === "Warehouses") {
        const data = await apiService.getWarehouses();
        setListData(data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoadingSection(false);
    }
  };

  const handlePrevPage = async () => {
    if (itemPage > 1) {
      await loadItemSummary(itemPage - 1);
    }
  };

  const handleNextPage = async () => {
    if (itemSummary?.pagination && itemPage < itemSummary.pagination.totalPages) {
      await loadItemSummary(itemPage + 1);
    }
  };

  const supplierTotalPages = Math.max(
    1,
    Math.ceil((listData?.length || 0) / supplierPageSize)
  );

  const paginatedSuppliers = useMemo(() => {
    if (selectedCard !== "Suppliers") return [];
    const start = (supplierPage - 1) * supplierPageSize;
    const end = start + supplierPageSize;
    return listData.slice(start, end);
  }, [listData, supplierPage, selectedCard]);

  const cards = [
    {
      title: "Total Items",
      value: summary?.total_items || 0,
      icon: <FaBoxOpen />,
      subtitle: "All registered inventory items",
    },
    {
      title: "Total Stock",
      value: summary?.total_stock || 0,
      icon: <FaWarehouse />,
      subtitle: "Current stock across warehouses",
    },
    {
      title: "Suppliers",
      value: summary?.total_suppliers || 0,
      icon: <FaUsers />,
      subtitle: "Active supplier records",
    },
    {
      title: "Pending POs",
      value: summary?.pending_po || 0,
      icon: <FaFileInvoice />,
      subtitle: "Purchase orders awaiting action",
    },
  ];

  return (
    <div className="dashboard-shell">
      <main className="dashboard-main">
        <div className="dashboard-topbar">
          <div>
            <h1 className="dashboard-title">Inventory Dashboard</h1>
            <p className="dashboard-subtitle">
              Monitor stock, suppliers, and purchasing performance in one place.
            </p>
          </div>
        </div>

        {loadingSummary ? (
          <SkeletonCards />
        ) : (
          <div className="dashboard-cards">
            {cards.map((card) => (
              <div
                key={card.title}
                className={`dashboard-card ${
                  selectedCard === card.title ? "active" : ""
                }`}
                onClick={() => handleCardClick(card.title)}
              >
                <div className="dashboard-card-top">
                  <div className="dashboard-icon-wrap">{card.icon}</div>
                  <span className="dashboard-chip">Live</span>
                </div>

                <h2>{card.title}</h2>
                <p>
                  <CountUp value={card.value} />
                </p>
                <small>{card.subtitle}</small>
              </div>
            ))}
          </div>
        )}

        {loadingSection && (
          <div className="section-loader-card">
            <div className="skeleton skeleton-line medium"></div>
            <div className="skeleton skeleton-line long"></div>
            <div className="skeleton skeleton-line long"></div>
            <div className="skeleton skeleton-line medium"></div>
          </div>
        )}

        {!loadingSection && selectedCard === "Total Items" && itemSummary && (
          <div className="list-container">
            <div className="section-header">
              <div>
                <h3>Total Items Overview</h3>
                <p>Quick summary of item stock distribution and item records.</p>
              </div>
            </div>

            <div className="dashboard-cards mini-cards">
              <div className="dashboard-card active stat-mini-card">
                <h2>All Items</h2>
                <p>{itemSummary.all_items ?? 0}</p>
              </div>

              <div className="dashboard-card active stat-mini-card">
                <h2>Highest Stock Item</h2>
                <p className="mini-card-text">
                  {itemSummary.highest_stock_item
                    ? `${itemSummary.highest_stock_item.itemName} (${itemSummary.highest_stock_item.total_stock})`
                    : "No data"}
                </p>
              </div>

              <div className="dashboard-card active stat-mini-card">
                <h2>Lowest Stock Item</h2>
                <p className="mini-card-text">
                  {itemSummary.lowest_stock_item
                    ? `${itemSummary.lowest_stock_item.itemName} (${itemSummary.lowest_stock_item.total_stock})`
                    : "No data"}
                </p>
              </div>
            </div>

            <div className="section-header">
              <div>
                <h3>All Items</h3>
                <p>Paginated item stock summary from the API.</p>
              </div>
            </div>

            <div className="table-wrap">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Catalog No</th>
                    <th>Item Name</th>
                    <th>Total Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {itemSummary.items && itemSummary.items.length > 0 ? (
                    itemSummary.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.catalogNo || "-"}</td>
                        <td>{item.itemName}</td>
                        <td>{item.total_stock}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="empty-cell">
                        No items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination-bar">
              <button onClick={handlePrevPage} disabled={itemPage <= 1}>
                <FaChevronLeft />
                Previous
              </button>

              <span>
                Page {itemSummary.pagination?.page || 1} of{" "}
                {itemSummary.pagination?.totalPages || 1}
              </span>

              <button
                onClick={handleNextPage}
                disabled={
                  !itemSummary.pagination ||
                  itemPage >= itemSummary.pagination.totalPages
                }
              >
                Next
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}

        {!loadingSection && selectedCard === "Total Stock" && (
          <div className="chart-container">
            <div className="section-header">
              <div>
                <h3>Total Stock Overview</h3>
                <p>Warehouse stock share based on current inventory totals.</p>
              </div>
            </div>

            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="total"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={85}
                    outerRadius={135}
                    paddingAngle={3}
                    label={({ name, percent }) =>
                      percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ""
                    }
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={colors[index % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}`, "Stock"]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="empty-state">No stock data found.</p>
            )}
          </div>
        )}

        {!loadingSection && selectedCard === "Suppliers" && (
          <div className="list-container">
            <div className="section-header">
              <div>
                <h3>Suppliers List</h3>
                <p>
                  Showing {paginatedSuppliers.length} of {listData.length} suppliers.
                </p>
              </div>
            </div>

            <div className="table-wrap">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Supplier Name</th>
                    <th>Address</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSuppliers.length > 0 ? (
                    paginatedSuppliers.map((row, i) => (
                      <tr key={i}>
                        <td>{row.SupplierName}</td>
                        <td>{row.Address || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="empty-cell">
                        No suppliers found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination-bar">
              <button
                onClick={() => setSupplierPage((prev) => Math.max(prev - 1, 1))}
                disabled={supplierPage <= 1}
              >
                <FaChevronLeft />
                Previous
              </button>

              <span>
                Page {supplierPage} of {supplierTotalPages}
              </span>

              <button
                onClick={() =>
                  setSupplierPage((prev) =>
                    Math.min(prev + 1, supplierTotalPages)
                  )
                }
                disabled={supplierPage >= supplierTotalPages}
              >
                Next
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}

        {!loadingSection && selectedCard === "Pending POs" && (
          <div className="list-container">
            <div className="section-header">
              <div>
                <h3>Pending Purchase Orders</h3>
                <p>Purchase orders currently waiting for processing.</p>
              </div>
            </div>

            <div className="table-wrap">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>PO ID</th>
                    <th>PO Number</th>
                    <th>Supplier</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listData.length > 0 ? (
                    listData.map((row, i) => (
                      <tr key={i}>
                        <td>{row.POID}</td>
                        <td>{row.PONumber}</td>
                        <td>{row.SupplierName}</td>
                        <td>
                          <span className="status-badge pending">{row.Status}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="empty-cell">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}